import { useEffect, useState } from "react";
import { useContent } from "../../../context/ContentContext";
import { Editable } from "../../../context/design";

export default function HeroSection() {
  const { hero } = useContent();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <section id="hero" className="relative min-h-screen w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={hero.image || "https://readdy.ai/api/search-image?query=peaceful%20meditative%20scene%20soft%20light%20blue%20tones%20ethereal%20misty%20landscape%20with%20gentle%20water%20reflections%20and%20soft%20glowing%20light%20serene%20atmosphere%20minimalist%20artistic%20style%20calming%20and%20warm%20emotional%20wellness%20aesthetic%20high%20quality&width=1920&height=1080&seq=hero1&orientation=landscape"}
          alt="Friedliche Kulisse"
          className="w-full h-full object-cover"
          style={{ objectPosition: "top" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-sky-100/40 via-sky-50/30 to-white/80"></div>
      </div>

      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-transparent"}`}>
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          <div className="flex items-center justify-between h-16 md:h-20">
            <a href="#hero" onClick={(e) => { e.preventDefault(); scrollTo("hero"); }}
              className="text-lg md:text-xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              <Editable as="span" id="hero.brand" label="Marke (Navigation)" className="text-sky-900">{hero.title_line1}</Editable>
              <span className="text-sky-500">.</span>
            </a>
            <div className="hidden md:flex items-center gap-8">
              {[["about","Über den Workshop"],["features","Was dich erwartet"],["details","Details"]] .map(([id, label]) => (
                <a key={id} href={`#${id}`} onClick={(e) => { e.preventDefault(); scrollTo(id); }}
                  className="text-sm font-medium text-sky-800 transition-colors hover:text-sky-600">{label}</a>
              ))}
              <a href="#cta" onClick={(e) => { e.preventDefault(); scrollTo("cta"); }}
                className="px-5 py-2.5 bg-sky-700 text-white text-sm font-medium rounded-full hover:bg-sky-800 transition-colors whitespace-nowrap">
                {hero.btn_primary}
              </a>
            </div>
            <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center text-sky-800" aria-label="Menü">
              <i className={`ri-${menuOpen ? "close" : "menu"}-line text-xl`}></i>
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-md border-t border-sky-100">
            <div className="px-4 py-4 space-y-3">
              {[["about","Über den Workshop"],["features","Was dich erwartet"],["details","Details"],["cta","Jetzt anmelden"]].map(([id, label]) => (
                <a key={id} href={`#${id}`} onClick={(e) => { e.preventDefault(); scrollTo(id); }}
                  className="block text-sm font-medium text-sky-800 py-2">{label}</a>
              ))}
            </div>
          </div>
        )}
      </nav>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 sm:px-6 pt-20">
        <div className="max-w-4xl text-center">
          <p className="text-sm md:text-base text-sky-700 font-medium tracking-widest uppercase mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
            Online-Workshop
          </p>
          <Editable as="h1" id="hero.title" label="Hauptüberschrift" className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-sky-950 leading-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            {hero.title_line1}<br />
            <span className="text-sky-700">{hero.title_line2}</span><br />
            {hero.title_line3}
          </Editable>
          <Editable as="p" id="hero.subtitle" label="Untertitel" className="text-lg md:text-xl text-sky-800/80 max-w-2xl mx-auto mb-10 leading-relaxed">{hero.subtitle}</Editable>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="#cta" onClick={(e) => { e.preventDefault(); scrollTo("cta"); }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-sky-800 text-white text-base font-medium rounded-full hover:bg-sky-900 transition-colors whitespace-nowrap">
              <Editable as="span" id="hero.btn_primary" label="Button 1">{hero.btn_primary}</Editable> <i className="ri-arrow-right-line"></i>
            </a>
            <a href="#about" onClick={(e) => { e.preventDefault(); scrollTo("about"); }}
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-sky-800 text-sky-800 text-base font-medium rounded-full hover:bg-sky-800 hover:text-white transition-colors whitespace-nowrap">
              <Editable as="span" id="hero.btn_secondary" label="Button 2">{hero.btn_secondary}</Editable>
            </a>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <a href="#about" onClick={(e) => { e.preventDefault(); scrollTo("about"); }} className="text-sky-700 hover:text-sky-900 transition-colors">
            <i className="ri-arrow-down-line text-2xl"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
